/*
	Сохранение черновиков ввода для существующих чатов (по session_id).
	Хранится единым массивом с временем изменения — чтобы чат можно было
	сортировать в истории по времени черновика, а не по последнему сообщению.
*/

export const INPUT_DRAFT_CHANGED_EVENT = "ba-chat-input-draft-changed";

export const SESSION_INPUT_DRAFTS_KEY = "ba-session-input-drafts";

// Legacy-формат: отдельный ключ-строка на каждую сессию
const LEGACY_DRAFT_PREFIX = "ba-chat-input-draft-";

export type SessionInputDraft = {
	session_id: string;
	message_text: string;
	updated_at: string;
};

const safeNowIso = (): string => new Date().toISOString();

const dispatchChanged = (sessionId: string) => {
	window.dispatchEvent(
		new CustomEvent(INPUT_DRAFT_CHANGED_EVENT, { detail: sessionId }),
	);
};

const isSessionInputDraft = (value: unknown): value is SessionInputDraft => {
	if (!value || typeof value !== "object") return false;
	const record = value as Record<string, unknown>;
	return (
		typeof record.session_id === "string" &&
		typeof record.message_text === "string" &&
		typeof record.updated_at === "string"
	);
};

// Одноразовый перенос старых ключей ba-chat-input-draft-<id> в единый массив
const migrateLegacyDrafts = (drafts: SessionInputDraft[]): boolean => {
	let migrated = false;
	const legacyKeys: string[] = [];
	for (let i = 0; i < localStorage.length; i++) {
		const key = localStorage.key(i);
		if (key && key.startsWith(LEGACY_DRAFT_PREFIX)) {
			legacyKeys.push(key);
		}
	}
	for (const key of legacyKeys) {
		const sessionId = key.slice(LEGACY_DRAFT_PREFIX.length);
		const text = localStorage.getItem(key) ?? "";
		localStorage.removeItem(key);
		if (!sessionId || text.trim().length === 0) continue;
		const existingIndex = drafts.findIndex((d) => d.session_id === sessionId);
		const entry: SessionInputDraft = {
			session_id: sessionId,
			message_text: text,
			updated_at: safeNowIso(),
		};
		if (existingIndex >= 0) {
			drafts[existingIndex] = entry;
		} else {
			drafts.push(entry);
		}
		migrated = true;
	}
	return migrated;
};

export const loadSessionInputDrafts = (): SessionInputDraft[] => {
	let drafts: SessionInputDraft[] = [];
	try {
		const raw = localStorage.getItem(SESSION_INPUT_DRAFTS_KEY);
		if (raw) {
			const parsed: unknown = JSON.parse(raw);
			if (Array.isArray(parsed)) {
				drafts = parsed.filter(isSessionInputDraft);
			}
		}
	} catch {
		drafts = [];
	}

	const migrated = migrateLegacyDrafts(drafts);
	if (migrated) {
		try {
			localStorage.setItem(
				SESSION_INPUT_DRAFTS_KEY,
				JSON.stringify(drafts),
			);
		} catch {
			/* ignore quota errors */
		}
	}
	return drafts;
};

const saveSessionInputDrafts = (drafts: SessionInputDraft[]): void => {
	try {
		localStorage.setItem(SESSION_INPUT_DRAFTS_KEY, JSON.stringify(drafts));
	} catch {
		/* ignore quota errors */
	}
};

export const getSessionInputDraft = (
	sessionId: string,
): SessionInputDraft | null =>
	loadSessionInputDrafts().find((d) => d.session_id === sessionId) ?? null;

export const getSessionInputDraftTime = (
	sessionId: string,
): string | null => getSessionInputDraft(sessionId)?.updated_at ?? null;

export const getInputDraft = (sessionId: string): string =>
	getSessionInputDraft(sessionId)?.message_text ?? "";

export const hasInputDraft = (sessionId: string): boolean =>
	getInputDraft(sessionId).trim().length > 0;

export const setInputDraft = (sessionId: string, draft: string) => {
	const drafts = loadSessionInputDrafts();
	const existingIndex = drafts.findIndex((d) => d.session_id === sessionId);

	if (draft === "") {
		if (existingIndex >= 0) {
			drafts.splice(existingIndex, 1);
			saveSessionInputDrafts(drafts);
		}
		dispatchChanged(sessionId);
		return;
	}

	const entry: SessionInputDraft = {
		session_id: sessionId,
		message_text: draft,
		updated_at: safeNowIso(),
	};
	if (existingIndex >= 0) {
		drafts[existingIndex] = entry;
	} else {
		drafts.push(entry);
	}
	saveSessionInputDrafts(drafts);
	dispatchChanged(sessionId);
};
